import React from "react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export function PinPad({ value, onChange, testid = "pin-pad", autoFocus = false }) {
  return (
    <div data-testid={testid}>
      <InputOTP maxLength={6} value={value} onChange={(v) => onChange(v.replace(/\D/g, ""))} autoFocus={autoFocus} inputMode="numeric">
        <InputOTPGroup>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg border-[color:var(--ek-border)]" />
          ))}
        </InputOTPGroup>
      </InputOTP>
    </div>
  );
}

export function PinSetupDialog({ open, onOpenChange, hasPin, onDone }) {
  const [pin, setPin] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const reset = () => { setPin(""); setConfirm(""); setError(""); };

  const submit = async () => {
    setError("");
    if (pin.length !== 6) { setError("PIN must be 6 digits"); return; }
    if (pin !== confirm) { setError("PINs do not match"); return; }
    setBusy(true);
    try {
      await api.post("/vault/set-pin", { pin });
      toast.success(hasPin ? "Vault PIN updated" : "Vault PIN set");
      reset();
      onOpenChange(false);
      onDone && onDone();
    } catch {
      setError("Could not save PIN");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
            <DialogTitle className="font-head font-semibold tracking-tight">{hasPin ? "Change Vault PIN" : "Set your Vault PIN"}</DialogTitle>
          </div>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
            A separate 6-digit PIN locks your Evidence Locker and authorizes every export.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-5">
          <div className="space-y-2">
            <span className="ek-mono-label">ENTER PIN</span>
            <PinPad value={pin} onChange={setPin} testid="pin-setup-input" autoFocus />
          </div>
          <div className="space-y-2">
            <span className="ek-mono-label">CONFIRM PIN</span>
            <PinPad value={confirm} onChange={setConfirm} testid="pin-confirm-input" />
          </div>
          {error && <p className="text-sm" style={{ color: "var(--ek-error)" }}>{error}</p>}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="pin-setup-submit" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            {busy ? "SAVING..." : hasPin ? "Update PIN" : "Set PIN"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
